/*
 * Amada Tracking V2
 *
 * Canonical storefront event + attribution layer. Business events are created
 * here first; PostHog, Shopify Customer Events, Google and OpenAI are adapters.
 * No destination secret belongs in this file.
 */
(function amadaTrackingV2() {
  'use strict';

  if (window.__amadaTrackingV2Loaded) return;
  if (window.Shopify && window.Shopify.designMode) return;
  if (document.documentElement.classList.contains('shopify-design-mode')) return;
  window.__amadaTrackingV2Loaded = true;

  const SCHEMA_VERSION = 1;
  const STORAGE_KEY = 'amada_attribution_v1';
  const SESSION_KEY = 'amada_tracking_session_v1';
  const ATTRIBUTION_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
  const FEE_HANDLE = 'amada-hand-engraving';
  const ADD_CONTEXT_MS = 10000;
  const PAID_MEDIA = new Set([
    'cpc',
    'ppc',
    'paid',
    'paid_search',
    'paidsearch',
    'paid-social',
    'paid_social',
    'display',
  ]);
  const ATTRIBUTION_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_id',
    'utm_term',
    'utm_content',
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'oppref',
  ];

  let pendingAdd = null;
  let productViewed = false;
  let cartSyncFingerprint = '';

  function cleanObject(input) {
    const output = {};
    Object.keys(input || {}).forEach((key) => {
      const value = input[key];
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value)) {
        if (value.length) output[key] = value;
        return;
      }
      if (typeof value === 'object') {
        const nested = cleanObject(value);
        if (Object.keys(nested).length) output[key] = nested;
        return;
      }
      output[key] = value;
    });
    return output;
  }

  function safeJsonParse(value) {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }

  function safeStorageGet(storage, key) {
    try {
      return storage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safeStorageSet(storage, key, value) {
    try {
      storage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  function randomId(prefix) {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return `${prefix}_${window.crypto.randomUUID()}`;
    }
    const random = Math.random().toString(36).slice(2);
    return `${prefix}_${Date.now().toString(36)}_${random}`;
  }

  function readCookie(name) {
    const prefix = `${name}=`;
    const parts = String(document.cookie || '').split(';');
    for (let index = 0; index < parts.length; index += 1) {
      const item = parts[index].trim();
      if (item.startsWith(prefix)) return decodeURIComponent(item.slice(prefix.length));
    }
    return '';
  }

  function customerPrivacy() {
    return window.Shopify && window.Shopify.customerPrivacy;
  }

  function analyticsAllowed() {
    const privacy = customerPrivacy();
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return false;
    try {
      return Boolean(privacy.analyticsProcessingAllowed());
    } catch (error) {
      return false;
    }
  }

  function marketingAllowed() {
    const privacy = customerPrivacy();
    if (!privacy || typeof privacy.marketingAllowed !== 'function') return false;
    try {
      return Boolean(privacy.marketingAllowed());
    } catch (error) {
      return false;
    }
  }

  function queryValue(params, name) {
    const values = params.getAll(name).filter(Boolean);
    if (!values.length) return '';
    // Shopify/Google Shopping can produce duplicate UTM keys where the paid
    // values are appended after product_sync/sag_organic values. Last wins.
    return values[values.length - 1];
  }

  function parseTouch(url, referrer) {
    let parsed;
    try {
      parsed = new URL(url, window.location.origin);
    } catch (error) {
      return {};
    }

    const params = parsed.searchParams;
    const touch = {
      landing_url: parsed.href,
      referrer: referrer || '',
      seen_at: new Date().toISOString(),
    };

    ATTRIBUTION_PARAMS.forEach((name) => {
      const value = queryValue(params, name);
      if (value) touch[name] = value;
    });

    const obref = readCookie('__obref');
    if (obref) touch.obref = obref;

    return cleanObject(touch);
  }

  function isPaidTouch(touch) {
    if (!touch) return false;
    if (touch.gclid || touch.gbraid || touch.wbraid || touch.oppref || touch.obref || touch.fbclid) return true;
    const medium = String(touch.utm_medium || '').trim().toLowerCase();
    return PAID_MEDIA.has(medium);
  }

  function loadAttribution() {
    const raw = safeStorageGet(window.localStorage, STORAGE_KEY);
    const stored = raw ? safeJsonParse(raw) : null;
    if (!stored || !stored.updated_at) return {};
    const updatedAt = Date.parse(stored.updated_at);
    if (!Number.isFinite(updatedAt) || Date.now() - updatedAt > ATTRIBUTION_MAX_AGE_MS) return {};
    return stored;
  }

  function getSession() {
    const raw = safeStorageGet(window.sessionStorage, SESSION_KEY);
    const stored = raw ? safeJsonParse(raw) : null;
    if (stored && stored.session_id) return stored;

    const created = {
      session_id: randomId('as'),
      started_at: new Date().toISOString(),
      landing_url: window.location.href,
      referrer: document.referrer || '',
    };
    safeStorageSet(window.sessionStorage, SESSION_KEY, JSON.stringify(created));
    return created;
  }

  function refreshAttribution() {
    const existing = loadAttribution();
    const now = new Date().toISOString();
    const session = getSession();
    const next = Object.assign({}, existing);

    if (!next.attribution_id) next.attribution_id = randomId('aa');
    if (!next.created_at) next.created_at = now;
    next.updated_at = now;
    next.session = session;

    if (analyticsAllowed() && !next.first_visit) {
      next.first_visit = cleanObject({
        landing_url: window.location.href,
        referrer: document.referrer || '',
        seen_at: now,
      });
    }

    if (marketingAllowed()) {
      const touch = parseTouch(window.location.href, document.referrer || '');
      const hasMarketingData = ATTRIBUTION_PARAMS.some((name) => touch[name]) || touch.obref;

      if (hasMarketingData) {
        if (!next.first_touch) next.first_touch = touch;
        if (isPaidTouch(touch)) next.latest_paid_touch = touch;
      }
    }

    safeStorageSet(window.localStorage, STORAGE_KEY, JSON.stringify(next));
    return next;
  }

  let attribution = refreshAttribution();

  function currentAttribution() {
    return attribution || {};
  }

  function pageContext() {
    return cleanObject({
      url: window.location.href,
      path: window.location.pathname,
      referrer: document.referrer || '',
      title: document.title || '',
    });
  }

  function readProduct() {
    const node = document.getElementById('amada-posthog-product');
    if (!node) return null;
    const parsed = safeJsonParse(node.textContent || '');
    return parsed && typeof parsed === 'object' ? parsed : null;
  }

  function currencyCode() {
    const shopifyCurrency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (shopifyCurrency) return shopifyCurrency;
    const product = readProduct();
    return product && product.currency;
  }

  function majorPrice(cents) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return undefined;
    return Math.round(amount) / 100;
  }

  function selectedVariantId() {
    const forms = document.querySelectorAll('product-form-component');
    for (let index = 0; index < forms.length; index += 1) {
      const form = forms[index];
      if (form.closest('.quick-add')) continue;
      const input = form.querySelector('input[name="id"]');
      if (input && input.value) return input.value;
    }
    return '';
  }

  function findVariant(product, variantId) {
    const variants = product && Array.isArray(product.variants) ? product.variants : [];
    if (!variants.length) return null;
    if (variantId) {
      const match = variants.find((variant) => String(variant.variant_id) === String(variantId));
      if (match) return match;
    }
    return variants[0];
  }

  function productContext(variantId) {
    const product = readProduct();
    if (!product) return {};
    const variant = findVariant(product, variantId);
    const commerce = {
      currency: product.currency || currencyCode(),
      product_id: product.product_id,
      product_handle: product.product_handle,
      product_title: product.product_title,
      collection: product.collection,
    };

    if (variant) {
      commerce.variant_id = variant.variant_id;
      commerce.sku = variant.sku;
      const price = majorPrice(variant.price);
      if (price !== undefined) commerce.value = price;
    }

    return cleanObject(commerce);
  }

  function buttonLocation(element) {
    if (!element || !element.closest) return undefined;
    if (element.closest('[data-amada-contact-launcher]')) return 'contact_launcher';
    if (element.closest('[data-amada-high-value-enquiry]')) return 'high_value_enquiry';
    if (element.closest('.amada-aside')) return 'product_aside';
    if (element.closest('.amada-booking')) return 'booking';
    if (element.closest('header-component, #header-group, header')) return 'header';
    if (element.closest('#footer-group, footer')) return 'footer';
    const section = element.closest('[id^="shopify-section-"]');
    if (section && section.id) return section.id.replace(/^shopify-section-/, '');
    return 'page';
  }

  function flattenAttributionForTransport(source) {
    const result = {};
    const first = source.first_touch || {};
    const latest = source.latest_paid_touch || {};

    result.attribution_id = source.attribution_id;
    result.first_landing_url = first.landing_url || (source.first_visit && source.first_visit.landing_url);
    result.first_referrer = first.referrer || (source.first_visit && source.first_visit.referrer);
    result.first_seen_at = first.seen_at || (source.first_visit && source.first_visit.seen_at);

    ATTRIBUTION_PARAMS.forEach((name) => {
      if (first[name]) result[`first_${name}`] = first[name];
      if (latest[name]) result[`latest_${name}`] = latest[name];
    });
    if (first.obref) result.first_obref = first.obref;
    if (latest.obref) result.latest_obref = latest.obref;

    return cleanObject(result);
  }

  function buildEnvelope(eventName, payload) {
    const session = getSession();
    return cleanObject({
      schema_version: SCHEMA_VERSION,
      event_id: randomId('ae'),
      event_name: eventName,
      occurred_at: new Date().toISOString(),
      source: 'browser',
      session_id: session.session_id,
      page: pageContext(),
      attribution: marketingAllowed() ? flattenAttributionForTransport(currentAttribution()) : {},
      consent: { analytics: analyticsAllowed(), marketing: marketingAllowed() },
      payload: payload || {},
    });
  }

  function publishShopify(envelope) {
    if (!window.Shopify || !Shopify.analytics || typeof Shopify.analytics.publish !== 'function') return;
    const isMarketingEvent = [
      'whatsapp_click',
      'email_click',
      'phone_click',
      'live_chat_open',
      'appointment_click',
      'enquiry_click',
      'lead_created',
    ].includes(envelope.event_name);

    if (isMarketingEvent ? !marketingAllowed() : !analyticsAllowed()) return;

    try {
      Shopify.analytics.publish(`amada:${envelope.event_name}`, envelope);
    } catch (error) {
      // Shopify transport is best-effort. Canonical event dispatch still occurs.
    }
  }

  // The first-party ledger only accepts non-authoritative browser events. A client-side
  // form success is recorded as a diagnostic; lead_created is minted by the lead backend.
  const FIRST_PARTY_EVENT_NAMES = {
    lead_created: 'form_success_client',
  };

  function firstPartyEnvelope(envelope) {
    const mapped = FIRST_PARTY_EVENT_NAMES[envelope.event_name];
    if (!mapped) return envelope;
    return Object.assign({}, envelope, {
      event_name: mapped,
      payload: Object.assign({}, envelope.payload, { client_event_name: envelope.event_name }),
    });
  }

  function sendToFirstPartyEndpoint(envelope) {
    const config = window.AmadaTrackingConfig || {};
    const endpoint = config.browserEndpoint;
    if (!endpoint || typeof endpoint !== 'string') return;

    const body = JSON.stringify(firstPartyEnvelope(envelope));
    try {
      // text/plain keeps the beacon a CORS "simple" request. A JSON beacon needs a
      // credentialed preflight that the endpoint (correctly) does not allow, and
      // sendBeacon still returns true, so the event would be lost silently.
      if (navigator.sendBeacon) {
        const sent = navigator.sendBeacon(endpoint, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
        if (sent) return;
      }
    } catch (error) {
      // Fall through to fetch.
    }

    try {
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'omit',
      }).catch(() => {});
    } catch (error) {
      // Destination errors must never block the customer action.
    }
  }

  function track(eventName, payload) {
    if (!eventName || typeof eventName !== 'string') return '';
    const envelope = buildEnvelope(eventName, cleanObject(payload || {}));

    document.dispatchEvent(
      new CustomEvent('amada:tracking', {
        detail: envelope,
      })
    );

    publishShopify(envelope);
    sendToFirstPartyEndpoint(envelope);
    return envelope.event_id;
  }

  function cartAttributionAttributes() {
    if (!marketingAllowed()) return {};
    const data = flattenAttributionForTransport(currentAttribution());
    const latest = currentAttribution().latest_paid_touch || {};
    const first = currentAttribution().first_touch || {};

    return cleanObject({
      _amada_attribution_id: data.attribution_id,
      _amada_first_source: first.utm_source,
      _amada_first_medium: first.utm_medium,
      _amada_first_campaign: first.utm_campaign,
      _amada_first_term: first.utm_term,
      _amada_first_content: first.utm_content,
      _amada_latest_source: latest.utm_source,
      _amada_latest_medium: latest.utm_medium,
      _amada_latest_campaign: latest.utm_campaign,
      _amada_latest_term: latest.utm_term,
      _amada_latest_content: latest.utm_content,
      _amada_gclid: latest.gclid || first.gclid,
      _amada_gbraid: latest.gbraid || first.gbraid,
      _amada_wbraid: latest.wbraid || first.wbraid,
      _amada_oppref: latest.oppref || first.oppref,
      _amada_obref: latest.obref || first.obref,
      _amada_first_landing: data.first_landing_url,
    });
  }

  function syncCartAttribution() {
    const attributes = cartAttributionAttributes();
    if (!Object.keys(attributes).length) return Promise.resolve(false);

    const fingerprint = JSON.stringify(attributes);
    if (fingerprint === cartSyncFingerprint) return Promise.resolve(true);
    cartSyncFingerprint = fingerprint;

    return fetch(`${(typeof Theme !== 'undefined' && Theme.routes?.cart_url) || '/cart'}/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error('cart attribution sync failed');
        return true;
      })
      .catch(() => {
        cartSyncFingerprint = '';
        return false;
      });
  }

  function injectFormAttribution(form) {
    if (!form || !marketingAllowed()) return;
    const data = cartAttributionAttributes();
    const fields = {
      'contact[Attribution ID]': data._amada_attribution_id,
      'contact[First Source]': data._amada_first_source,
      'contact[First Medium]': data._amada_first_medium,
      'contact[First Campaign]': data._amada_first_campaign,
      'contact[Latest Source]': data._amada_latest_source,
      'contact[Latest Medium]': data._amada_latest_medium,
      'contact[Latest Campaign]': data._amada_latest_campaign,
      'contact[Google Click ID]': data._amada_gclid,
      'contact[OpenAI Ref]': data._amada_oppref,
    };

    Object.keys(fields).forEach((name) => {
      const value = fields[name];
      if (!value) return;
      let input = form.querySelector(`input[name="${CSS.escape(name)}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }
      input.value = value;
    });
  }

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function requestMethod(input, init) {
    const method = (init && init.method) || (input && input.method) || 'GET';
    return String(method).toUpperCase();
  }

  function isCartAdd(url) {
    try {
      return /\/cart\/add(\.js)?$/.test(new URL(url, window.location.origin).pathname);
    } catch (error) {
      return false;
    }
  }

  function isEngravingFee(item) {
    if (!item || typeof item !== 'object') return false;
    if (item.handle === FEE_HANDLE) return true;
    const properties = item.properties;
    if (!properties) return false;
    if (properties._for) return true;
    if (Array.isArray(properties)) {
      return properties.some((entry) => entry && (entry.name === '_for' || entry[0] === '_for'));
    }
    return false;
  }

  function addedItems(data) {
    if (!data || typeof data !== 'object') return [];
    if (Array.isArray(data.items)) return data.items;
    if (data.variant_id || data.product_id) return [data];
    return [];
  }

  function trackAddedItem(item) {
    if (isEngravingFee(item)) return;
    const payload = {
      commerce: {
        currency: currencyCode(),
        product_id: item.product_id,
        variant_id: item.variant_id,
        product_handle: item.handle,
        product_title: item.product_title || item.title,
        sku: item.sku,
        value: majorPrice(item.price),
        quantity: Number(item.quantity) || 1,
      },
      button_location: pendingAdd && Date.now() - pendingAdd.at < ADD_CONTEXT_MS ? pendingAdd.location : undefined,
    };
    track('add_to_cart', payload);
  }

  function inspectCartAdd(response) {
    let copy;
    try {
      copy = response.clone();
    } catch (error) {
      return;
    }

    copy
      .json()
      .then((data) => {
        if (!response.ok || !data || data.status) return;
        addedItems(data).forEach(trackAddedItem);
        syncCartAttribution();
      })
      .catch(() => {});
  }

  function installCartAddTracking() {
    if (!window.fetch || window.fetch.__amadaTrackingCart) return;
    const originalFetch = window.fetch.bind(window);
    const wrappedFetch = function amadaTrackingFetch(input, init) {
      const pending = originalFetch(input, init);
      if (requestMethod(input, init) !== 'POST' || !isCartAdd(requestUrl(input))) return pending;
      return pending.then((response) => {
        inspectCartAdd(response);
        return response;
      });
    };
    wrappedFetch.__amadaTrackingCart = true;
    window.fetch = wrappedFetch;
  }

  function rememberAddLocation(event) {
    if (!event.isTrusted) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const control = target.closest('button, a, input, [role="button"]');
    if (!control) return;

    let location = '';
    if (control.closest('sticky-add-to-cart')) location = 'sticky';
    else if (control.matches('[data-amada-add]') || control.closest('[data-amada-add]')) location = 'wishlist';
    else if (control.closest('.quick-add')) location = 'quick_add';
    else if (control.closest('product-form-component, form[action*="/cart/add"]')) location = 'product';
    if (location) pendingAdd = { location, at: Date.now() };
  }

  function isWhatsApp(href) {
    try {
      const host = new URL(href, window.location.origin).hostname.replace(/^www\./, '');
      return host === 'wa.me' || host === 'api.whatsapp.com' || host === 'whatsapp.com' || host.endsWith('.whatsapp.com');
    } catch (error) {
      return false;
    }
  }

  function isEmail(href) {
    return String(href || '').trim().toLowerCase().startsWith('mailto:');
  }

  function isTelephone(href) {
    return String(href || '').trim().toLowerCase().startsWith('tel:');
  }

  function isCalendly(href) {
    try {
      const url = new URL(href, window.location.origin);
      return url.hostname === 'calendly.com' || url.hostname.endsWith('.calendly.com');
    } catch (error) {
      return false;
    }
  }

  function isAppointmentLink(link, href) {
    if (!link) return false;
    if (isCalendly(href)) return true;
    if (link.matches('[data-amada-book-appointment], [data-amada-appointment]')) return true;
    try {
      const path = new URL(href, window.location.origin).pathname.toLowerCase();
      return path.includes('book-appointment') || path.includes('book-an-appointment');
    } catch (error) {
      return false;
    }
  }

  function trackLinkClick(event) {
    if (!event.isTrusted) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const link = target.closest('a[href]');
    if (!link) return;

    const href = link.getAttribute('href') || '';
    let channel;
    let name;
    if (isWhatsApp(href)) [name, channel] = ['whatsapp_click', 'whatsapp'];
    else if (isEmail(href)) [name, channel] = ['email_click', 'email'];
    else if (isTelephone(href)) [name, channel] = ['phone_click', 'phone'];
    else if (isAppointmentLink(link, href)) [name, channel] = ['appointment_click', 'calendly'];
    else return;

    // Ordinary link clicks bail out above; placement and product lookups only
    // run for the few links that are actually tracked.
    track(name, {
      channel,
      button_location: buttonLocation(link),
      commerce: productContext(selectedVariantId()),
    });
  }

  function trackControlClick(event) {
    if (!event.isTrusted) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-amada-contact-chat], [data-amada-contact-reply]')) {
      track('live_chat_open', {
        channel: 'chat',
        button_location: 'contact_launcher',
        commerce: productContext(selectedVariantId()),
      });
      return;
    }

    const submit = target.closest('button, input[type="submit"]');
    if (!submit) return;
    if (submit.closest('.email-signup, .email-signup__form')) return;

    const form = submit.closest('form');
    if (form) injectFormAttribution(form);

    if (submit.closest('.amada-hulk')) {
      track('enquiry_click', {
        channel: 'form',
        form_type: 'hulk',
        button_location: buttonLocation(submit),
        commerce: productContext(selectedVariantId()),
      });
      return;
    }

    if (form && (form.matches('.amada-booking__form') || form.matches('form.contact-form__form'))) {
      track('enquiry_click', {
        channel: 'form',
        form_type: form.matches('.amada-booking__form') ? 'booking_request' : 'contact',
        button_location: buttonLocation(submit),
        commerce: productContext(selectedVariantId()),
      });
    }
  }

  function trackProductView() {
    if (productViewed) return;
    const product = readProduct();
    if (!product) return;
    productViewed = true;
    track('product_viewed', { commerce: productContext(selectedVariantId()) });
  }

  function successLeadId(prefix, node) {
    if (node && node.dataset && node.dataset.amadaLeadId) return node.dataset.amadaLeadId;
    const id = randomId(prefix);
    if (node && node.dataset) node.dataset.amadaLeadId = id;
    return id;
  }

  // A posted contact form reloads the page with the thank-you already rendered, often
  // before Shopify's consent API has loaded. Reporting it then would drop the ad source
  // and the server hand-off, so wait for consent (at most CONSENT_WAIT_MS).
  const CONSENT_WAIT_MS = 3000;
  let consentSettled = Boolean(customerPrivacy());

  function afterConsentSettles(callback) {
    if (consentSettled) {
      callback();
      return;
    }
    let done = false;
    const run = () => {
      if (done) return;
      done = true;
      consentSettled = true;
      attribution = refreshAttribution();
      callback();
    };
    whenConsentApiReady(run);
    setTimeout(run, CONSENT_WAIT_MS);
  }

  function detectContactSuccesses() {
    if (!consentSettled) return;
    document.querySelectorAll('.amada-booking__success, .contact-form__success').forEach((node) => {
      if (node.dataset.amadaTrackingSuccess === 'true') return;
      if (node.closest('.email-signup, .email-signup__form')) return;
      node.dataset.amadaTrackingSuccess = 'true';

      const bookingRequest = Boolean(node.closest('.amada-booking'));
      track('lead_created', {
        lead_id: successLeadId('lead_contact', node),
        channel: 'form',
        form_type: bookingRequest ? 'booking_request' : 'contact',
        button_location: buttonLocation(node),
        commerce: productContext(selectedVariantId()),
      });
    });
  }

  function trackHulkSuccess(event) {
    const detail = event.detail || {};
    const fields = detail.fields || {};
    const root = event.target instanceof Element ? event.target : null;

    track('lead_created', {
      lead_id: fields.lead_id || randomId('lead_hulk'),
      channel: 'form',
      form_type: 'hulk',
      form_id: detail.form || undefined,
      button_location: buttonLocation(root),
      commerce: cleanObject({
        product_title: fields.piece_title,
        variant_title: fields.piece_variant,
        sku: fields.piece_sku,
      }),
    });
  }

  function trackCalendlyClientConfirmation(event) {
    if (event.origin !== 'https://calendly.com') return;
    const data = event.data;
    if (!data || data.event !== 'calendly.event_scheduled') return;

    // Diagnostic only. A real appointment_booked conversion must be created by
    // the Calendly webhook so new-tab/fallback bookings cannot be missed.
    track('appointment_client_confirmed', {
      channel: 'calendly',
      booking_method: 'embed',
      button_location: 'booking',
    });
  }

  function enrichCalendlyUrl(inputUrl) {
    let url;
    try {
      url = new URL(inputUrl, window.location.origin);
    } catch (error) {
      return inputUrl;
    }

    const data = currentAttribution();
    const touch = data.latest_paid_touch || data.first_touch || {};
    const attributionId = data.attribution_id || '';

    if (touch.utm_source) url.searchParams.set('utm_source', touch.utm_source);
    else if (!url.searchParams.get('utm_source')) url.searchParams.set('utm_source', 'amada');

    if (touch.utm_medium) url.searchParams.set('utm_medium', touch.utm_medium);
    else if (!url.searchParams.get('utm_medium')) url.searchParams.set('utm_medium', 'booking');

    if (touch.utm_campaign) url.searchParams.set('utm_campaign', touch.utm_campaign);
    else if (!url.searchParams.get('utm_campaign')) url.searchParams.set('utm_campaign', 'book-appointment');

    if (touch.utm_term) url.searchParams.set('utm_term', touch.utm_term);

    const existingContent = touch.utm_content || url.searchParams.get('utm_content') || '';
    const token = attributionId ? `amada_aid_${attributionId}` : '';
    const content = [existingContent, token].filter(Boolean).join('|');
    if (content) url.searchParams.set('utm_content', content.slice(0, 240));

    return url.toString();
  }

  function refreshCalendlyEmbeds() {
    document.querySelectorAll('.amada-booking__calendly[data-url]').forEach((element) => {
      const original = element.dataset.baseUrl || element.dataset.url;
      if (!element.dataset.baseUrl) element.dataset.baseUrl = original;
      const enriched = enrichCalendlyUrl(original);
      element.dataset.url = enriched;
      const fallback = element.querySelector('a[href]');
      if (fallback && isCalendly(fallback.href)) fallback.href = enriched;
    });
  }

  // This script can run before Shopify's consent API has loaded. marketingAllowed() is then
  // false, and the landing page's UTMs/click ids would be dropped. visitorConsentCollected
  // only fires when the visitor makes a choice, so also re-read attribution once the API is
  // ready (still on the landing URL).
  function whenConsentApiReady(callback) {
    if (customerPrivacy()) return;
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error) callback();
      });
    } catch (error) {
      // Consent API unavailable: stay conservative.
    }
  }

  function handleConsentChange() {
    attribution = refreshAttribution();
    refreshCalendlyEmbeds();
    if (marketingAllowed()) syncCartAttribution();
  }

  installCartAddTracking();
  document.addEventListener('click', rememberAddLocation, true);
  document.addEventListener('click', trackLinkClick, true);
  document.addEventListener('click', trackControlClick, true);
  document.addEventListener('amada:hulk:success', trackHulkSuccess);
  document.addEventListener('visitorConsentCollected', handleConsentChange);
  whenConsentApiReady(handleConsentChange);
  window.addEventListener('message', trackCalendlyClientConfirmation);

  // Success panels only appear after a form submit, so check once per idle slot
  // instead of running a page-wide query inside every interaction's frame.
  let successCheckQueued = false;
  const queueSuccessCheck = () => {
    if (successCheckQueued) return;
    successCheckQueued = true;
    const run = () => {
      successCheckQueued = false;
      detectContactSuccesses();
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 1000 });
    else window.setTimeout(run, 250);
  };
  const successObserver = new MutationObserver(queueSuccessCheck);
  successObserver.observe(document.documentElement, { childList: true, subtree: true });

  trackProductView();
  afterConsentSettles(detectContactSuccesses);
  refreshCalendlyEmbeds();

  if (marketingAllowed()) syncCartAttribution();

  window.AmadaTracking = {
    track,
    getAttribution: function getAttribution() {
      return JSON.parse(JSON.stringify(currentAttribution()));
    },
    syncCartAttribution,
    enrichCalendlyUrl,
    refreshAttribution: function publicRefreshAttribution() {
      attribution = refreshAttribution();
      return this.getAttribution();
    },
    _test: {
      parseTouch,
      isPaidTouch,
      isWhatsApp,
      isEmail,
      isTelephone,
      isCalendly,
      flattenAttributionForTransport,
    },
  };
})();
